import { apiUrl } from '@/api/helper';
import { useAuthStore } from '@/stores/auth/auth.slice';
import { usesFakeData } from '../mode';
import type { PdsDevice } from './types';

const TIMEOUT_MS = 15_000;
const PAGE_SIZE = 200;

interface ApiDevice {
  id: string;
  type: string;
  role?: string | null;
  label: string;
  status: PdsDevice['status'];
  ip?: string | null;
  port?: number | null;
  location?: string | null;
  site?: { name?: string } | null;
  settings?: { agentScheme?: string } | null;
}

export const toPdsDevice = (device: ApiDevice): PdsDevice => ({
  id: device.id,
  type: device.type,
  role: device.role ?? null,
  label: device.label,
  status: device.status,
  ip: device.ip ?? null,
  port: device.port ?? null,
  location: device.location ?? null,
  site: device.site?.name ?? '',
  agentScheme: device.settings?.agentScheme === 'https' ? 'https' : 'http',
});

// Les appareils prévus ou installés du commerce connecté ; les retirés n'ont plus rien à faire ici.
// En mode démonstration il n'y a pas de serveur : la liste est vide plutôt qu'inventée.
export const fetchStoreDevices = async (): Promise<PdsDevice[]> => {
  if (usesFakeData()) return [];

  const auth = useAuthStore();
  const storeId = auth.decodedJwt.accessToken?.restaurant_ref;
  const params = new URLSearchParams({
    pagination: 'true',
    page: '1',
    pageSize: String(PAGE_SIZE),
    sortBy: 'type',
    sortOrder: 'ASC',
  });
  if (storeId) params.append('storeIds', storeId);
  ['planned', 'installed'].forEach((status) =>
    params.append('statuses', status),
  );

  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), TIMEOUT_MS);
  try {
    const response = await fetch(`${apiUrl('/devices/search')}?${params}`, {
      signal: abort.signal,
      headers: {
        authorization: `Bearer ${auth.tokens.accessToken}`,
        'x-client-app': 'merchant-order-manager',
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const body = (await response.json()) as { data?: ApiDevice[] };
    return (Array.isArray(body.data) ? body.data : []).map(toPdsDevice);
  } finally {
    clearTimeout(timer);
  }
};
